import { FormControl } from '../../../widgets/formControl';
import { Input, SearchInput } from '../../../widgets/input';
import { Link } from '../../../widgets/buttons';
import { NotificationBanner } from '../../../widgets/notificationBanner';
import { Radio } from '../../../widgets/radio';
import React, { FC, useEffect, useState } from 'react';
import { ContainerCombo, SearchContainer } from 'src/components/pages/productConfiguration/steps/6-done/connectModelDialog';
import { PaletteType } from 'src/shared/enums';
import { T } from 'src/shared/helpers';
import { validateRequiredTrimmed } from 'src/shared/validation';
import theme from 'src/shared/theme';
import { styled } from 'styled-components';
import { ValidationBox } from '../../../widgets/validation/validationBox';
import { PaletteColorInput, PaletteInput } from './__generated__/paletteColorsPageSaveMutation.graphql';
import { PaletteColorsTable } from './paletteColorsTable';

interface PaletteManagerContentProps {
    currentPalette: PaletteInput;
    currentColors: PaletteColorInput[];
    onDelete?: (index: number) => void; // Callback for deleting a color
    onAdd?: () => void; // Callback for adding a new color
    onEdit?: (index: number, updatedColor: PaletteColorInput) => void; // Callback for editing a color
    onReset?: (index: number) => void; // Callback for resetting a color
    onPantoneIconClick?: (index: number, color: PaletteColorInput) => void; // Callback for Pantone icon click
    onPaletteNameChange: (name: string) => void;
    isZakekeDefault?: boolean;
    onHandleChooseFromLibraryClick?: () => void;
    onViewChange?: (view: string) => void;
    onSearchColor?: (search: string) => void; // Callback for search
    onChangeColorType?: (type: PaletteType) => void;
    searchValue?: string; // Current search value to make SearchInput controlled
    hasColors?: boolean; // To determine if we should show search
    shouldShowRadioButtons?: boolean; // To determine if we should show radio buttons
    isPending?: boolean;
    paletteNameValidationKey?: string;
    paletteColorsValidationKey?: string;
    paletteColorsValidationMessage?: string;
    validatePaletteColors?: (palette: PaletteInput) => boolean;
}

const StyledFormControl = styled(FormControl)`
    display: flex;
    align-items: flex-end;
    margin-bottom: 18px;
    width: 100%;

    & > div {
        width: 100%;
    }
`;

const BannerContainer = styled.div`
    margin-bottom: 24px;
`;

const RadioButtonsContainer = styled.div`
    display: flex;
    margin-bottom: 16px;
`;

const PaletteColorsContent: FC<PaletteManagerContentProps> = ({
    currentPalette,
    currentColors,
    onDelete,
    onAdd,
    onEdit,
    onReset,
    onPantoneIconClick,
    onPaletteNameChange,
    isZakekeDefault,
    onHandleChooseFromLibraryClick,
    onViewChange,
    onSearchColor,
    onChangeColorType,
    searchValue = '',
    hasColors = false,
    shouldShowRadioButtons = true,
    isPending,
    paletteNameValidationKey,
    paletteColorsValidationKey,
    paletteColorsValidationMessage,
    validatePaletteColors
}) => {
    const [showRadioButtons, setShowRadioButtons] = useState(shouldShowRadioButtons);

    // Use currentPalette.paletteTypeID directly instead of local state
    const handleColorTypeChange = (newType: PaletteType) => {
        onChangeColorType?.(newType);
    };

    useEffect(() => {
        if (!shouldShowRadioButtons) return;

        // Only show radio buttons if there's exactly one color and it's empty
        if (currentColors.length === 1) {
            const firstColor = currentColors[0];
            const isEmptyColor =
                (currentPalette.paletteTypeID === PaletteType.Pantone && (!firstColor.spotColorName || !firstColor.name)) ||
                (currentPalette.paletteTypeID === PaletteType.CMYK && (!firstColor.cmykHexCode || firstColor.cmykHexCode === '0, 0, 0, 0'));

            setShowRadioButtons(isEmptyColor);
        } else {
            setShowRadioButtons(false);
        }
    }, [currentColors, currentPalette.paletteTypeID]);

    const paletteColorsTable = (
        <PaletteColorsTable
            colors={currentColors}
            onAdd={onAdd}
            onDelete={onDelete}
            onEdit={onEdit}
            onReset={onReset}
            onPantoneIconClick={onPantoneIconClick}
            isZakekeDefault={currentPalette?.isZakekeDefault}
            paletteType={currentPalette.paletteTypeID || PaletteType.Pantone}
            isEditing={currentPalette?.id !== null && currentPalette?.id !== undefined}
            isPending={isPending}
        />
    );

    return (
        <>
            <BannerContainer>
                <NotificationBanner
                    type='INFO'
                    content={
                        <span>
                            {T._(
                                'Here you can define your own Pantone/CMYK to RGB Mapping Palette. For each row, specify the color (Pantone or CMYK), give it a name, and set its corresponding RGB value. This mapping ensures colors are displayed as accurately as possible on screen while allowing your customers to work in their chosen color system. ',
                                'Admin'
                            )}
                            <Link href='https://zakeke.zendesk.com/hc/en-us/articles/4402564616978-Pantone-CMYK-Mode' target='_blank'>
                                {T._('learn more', 'Admin')}
                            </Link>
                        </span>
                    }
                ></NotificationBanner>
            </BannerContainer>
            {hasColors ? (
                <ContainerCombo>
                    <SearchContainer gridTemplateColumns='1fr 448px'>
                        <FormControl label={T._('Palette Name', 'Admin')}>
                            <Input
                                value={currentPalette.name || ''}
                                onChange={(e) => onPaletteNameChange(e.target.value)}
                                placeholder={T._('Palette Name', 'Admin')}
                                disabled={currentPalette.isZakekeDefault}
                                validationKey={paletteNameValidationKey}
                                validationLabel={T._('Palette Name', 'Admin')}
                                validate={paletteNameValidationKey ? validateRequiredTrimmed : undefined}
                                validationMessage={T._('Palette name is required.', 'Admin')}
                            />
                        </FormControl>
                        <StyledFormControl label={''}>
                            <SearchInput
                                fullWidth
                                value={searchValue}
                                placeholder={T._('Search...', 'Admin')}
                                onChange={(e) => {
                                    onSearchColor?.(e.target.value);
                                }}
                            />
                        </StyledFormControl>
                    </SearchContainer>
                </ContainerCombo>
            ) : (
                <>
                    <FormControl label={T._('Palette Name', 'Admin')}>
                        <Input
                            value={currentPalette.name || ''}
                            onChange={(e) => onPaletteNameChange(e.target.value)}
                            placeholder={T._('Palette Name', 'Admin')}
                            validationKey={paletteNameValidationKey}
                            validationLabel={T._('Palette Name', 'Admin')}
                            validate={paletteNameValidationKey ? validateRequiredTrimmed : undefined}
                            validationMessage={T._('Palette name is required.', 'Admin')}
                        />
                    </FormControl>
                </>
            )}

            {showRadioButtons && (
                <RadioButtonsContainer>
                    <Radio
                        color={theme.app.secondary}
                        name='paletteColorType'
                        label={'Pantone'}
                        checked={currentPalette.paletteTypeID === PaletteType.Pantone}
                        value={(currentPalette.paletteTypeID === PaletteType.Pantone).toString()}
                        onSelect={(checked) => handleColorTypeChange(checked ? PaletteType.Pantone : PaletteType.CMYK)}
                    ></Radio>
                    <Radio
                        color={theme.app.secondary}
                        name='paletteColorType'
                        label={'CMYK'}
                        checked={currentPalette.paletteTypeID === PaletteType.CMYK}
                        value={(currentPalette.paletteTypeID === PaletteType.CMYK).toString()}
                        onSelect={(checked) => handleColorTypeChange(checked ? PaletteType.CMYK : PaletteType.Pantone)}
                    ></Radio>
                </RadioButtonsContainer>
            )}

            {paletteColorsValidationKey && validatePaletteColors ? (
                <ValidationBox
                    fullWidth
                    validationKey={paletteColorsValidationKey}
                    validationLabel={T._('Colors', 'Admin')}
                    validationValue={currentPalette}
                    validate={validatePaletteColors}
                    validationMessage={paletteColorsValidationMessage}
                >
                    {paletteColorsTable}
                </ValidationBox>
            ) : (
                paletteColorsTable
            )}
        </>
    );
};

export default PaletteColorsContent;
